const WORKY_SITE_ORIGIN: string = 'https://worky.pro';
const WORKY_ORG_NAME: string = 'Worky Software Solutions Inc.';
const WORKY_LOGO_URL: string = `${WORKY_SITE_ORIGIN}/worky-logo-for-media-sharing.jpeg`;

const buildOrganizationJsonLd = (): Readonly<Record<string, unknown>> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${WORKY_SITE_ORIGIN}/#organization`,
    name: WORKY_ORG_NAME,
    url: `${WORKY_SITE_ORIGIN}/`,
    logo: WORKY_LOGO_URL,
    description:
      'Healthcare staffing software and home care staffing software for agencies: scheduling, time clock, payroll, and client invoicing.',
    areaServed: [
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'AdministrativeArea', name: 'Ontario' },
      { '@type': 'City', name: 'Toronto' },
      { '@type': 'City', name: 'Mississauga' },
      { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
    ],
    sameAs: [
      'https://www.linkedin.com/company/worky-pro',
      'https://twitter.com/workypro',
      'https://www.instagram.com/workypro',
    ],
  };
};

const buildWebSiteJsonLd = (): Readonly<Record<string, unknown>> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${WORKY_SITE_ORIGIN}/#website`,
    name: 'Worky',
    url: `${WORKY_SITE_ORIGIN}/`,
    description:
      'Worky is healthcare staffing software and home care staffing software that helps agencies run leads, scheduling, time clock, payroll, and invoicing.',
    publisher: { '@id': `${WORKY_SITE_ORIGIN}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${WORKY_SITE_ORIGIN}/landing-page-blog?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

const buildSoftwareApplicationJsonLd = (
  name: string,
  description: string,
  urlPath: string
): Readonly<Record<string, unknown>> => {
  const normalizedPath: string = urlPath === '/' ? '/' : urlPath.startsWith('/') ? urlPath : `/${urlPath}`;
  const pageUrl: string =
    normalizedPath === '/' ? `${WORKY_SITE_ORIGIN}/` : `${WORKY_SITE_ORIGIN}${normalizedPath}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Healthcare Staffing Software',
    operatingSystem: 'Web, iOS, Android',
    url: pageUrl,
    image: WORKY_LOGO_URL,
    description,
    provider: { '@id': `${WORKY_SITE_ORIGIN}/#organization` },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'CAD',
      availability: 'https://schema.org/InStock',
      url: pageUrl,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '250',
      bestRating: '5',
      worstRating: '1',
    },
  };
};

const buildFaqPageJsonLd = (
  faqs: readonly { readonly question: string; readonly answer: string }[]
): Readonly<Record<string, unknown>> => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

const buildHomeCareServiceJsonLd = (
  faqs: readonly { readonly question: string; readonly answer: string }[]
): readonly Readonly<Record<string, unknown>>[] => {
  const serviceJsonLd: Readonly<Record<string, unknown>> = {
    '@context': 'https://schema.org',
    '@type': 'MedicalBusiness',
    '@id': `${WORKY_SITE_ORIGIN}/landing-page-homecare#homecare`,
    name: 'Worky Home Care',
    url: `${WORKY_SITE_ORIGIN}/landing-page-homecare`,
    image: WORKY_LOGO_URL,
    description:
      'Private home care near you across Toronto, Mississauga, and the GTA—including PSW home care, senior home care, respite care for an elderly parent, home care after hospital discharge, and nursing care at home.',
    provider: { '@id': `${WORKY_SITE_ORIGIN}/#organization` },
    areaServed: [
      { '@type': 'City', name: 'Toronto' },
      { '@type': 'City', name: 'Mississauga' },
      { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      { '@type': 'AdministrativeArea', name: 'Ontario' },
    ],
    availableService: [
      {
        '@type': 'Service',
        name: 'Private home care',
        serviceType: 'Private home care near me',
        areaServed: 'Greater Toronto Area',
      },
      {
        '@type': 'Service',
        name: 'PSW home care Mississauga',
        serviceType: 'Personal support worker home care',
        areaServed: 'Mississauga',
      },
      {
        '@type': 'Service',
        name: 'Senior home care Toronto',
        serviceType: 'Senior home care',
        areaServed: 'Toronto',
      },
      {
        '@type': 'Service',
        name: 'Respite care for elderly parent',
        serviceType: 'Respite care',
      },
      {
        '@type': 'Service',
        name: 'Home care after hospital discharge',
        serviceType: 'Post-discharge home care',
      },
      {
        '@type': 'Service',
        name: 'Nursing care at home GTA',
        serviceType: 'In-home nursing',
        areaServed: 'Greater Toronto Area',
      },
    ],
  };

  return [serviceJsonLd, buildFaqPageJsonLd(faqs)];
};

export {
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
  buildSoftwareApplicationJsonLd,
  buildFaqPageJsonLd,
  buildHomeCareServiceJsonLd,
};
